/**
 * ClientQueuePriorityEngine
 *
 * Pure functions — no Angular dependencies. Fully unit-testable.
 *
 * ## Scoring model
 *
 * Each factor contributes a fixed number of points to the row's priority score.
 * The top 2 contributing factors are surfaced in the rank-reason tooltip.
 *
 * | Factor                  | Points | Reason label            |
 * |-------------------------|--------|-------------------------|
 * | Escalated handoff       |  50    | "Escalated"             |
 * | Waiting > 20m           |  40    | "Waiting 20+ min"       |
 * | Needs FA handoff        |  35    | "Needs FA attention"    |
 * | Upcoming call < 30m     |  30    | "Call in 30 min"        |
 * | Waiting 10–20m          |  25    | "Waiting 10+ min"       |
 * | SLA overdue badge       |  20    | "SLA overdue"           |
 * | Client is active now    |  10    | "Client is active now"  |
 *
 * ## Bucket thresholds
 * - score >= 50 → Now
 * - score >= 20 → Next
 * - else → Later
 */

import {
  EnrichedQueueRow,
  QueueBucket,
  QueueFilter,
  QueueSection,
  RankReason,
} from './client-queue.model';

// ---------------------------------------------------------------------------
// Scoring factors
// ---------------------------------------------------------------------------

interface ScoringFactor {
  label: string;
  points: number;
  applies: (row: EnrichedQueueRow) => boolean;
}

export const SCORING_FACTORS: ScoringFactor[] = [
  {
    label: 'Escalated',
    points: 50,
    applies: (r) => r.handoff === 'escalated',
  },
  {
    label: 'Waiting 20+ min',
    points: 40,
    applies: (r) => r.waitingMinutes > 20,
  },
  {
    label: 'Needs FA attention',
    points: 35,
    applies: (r) => r.handoff === 'needs_fa',
  },
  {
    label: 'Call in 30 min',
    points: 30,
    applies: (r) => r.hasUpcomingCall && minutesUntilCall(r) <= 30,
  },
  {
    label: 'Waiting 10+ min',
    points: 25,
    applies: (r) => r.waitingMinutes >= 10 && r.waitingMinutes <= 20,
  },
  {
    label: 'SLA overdue',
    points: 20,
    applies: (r) => r.urgency === 'overdue',
  },
  {
    label: 'Client is active now',
    points: 10,
    applies: (r) => r.presence === 'active',
  },
];

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function minutesUntilCall(row: EnrichedQueueRow): number {
  if (!row.upcomingCallAt) return Infinity;
  return Math.max(0, (new Date(row.upcomingCallAt).getTime() - Date.now()) / 60000);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compute the total priority score for a row.
 * Returns the sum of all applicable factor points.
 */
export function scoreRow(row: EnrichedQueueRow): number {
  return SCORING_FACTORS.filter((f) => f.applies(row)).reduce((sum, f) => sum + f.points, 0);
}

/**
 * Determine which bucket a score belongs to.
 */
export function bucketFromScore(score: number): QueueBucket {
  if (score >= 50) return 'now';
  if (score >= 20) return 'next';
  return 'later';
}

/**
 * Return the top 2 contributing factors for the rank-reason tooltip.
 */
export function topReasons(row: EnrichedQueueRow): RankReason[] {
  return SCORING_FACTORS
    .filter((f) => f.applies(row))
    .map((f) => ({ label: f.label, weight: f.points }))
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 2);
}

/**
 * Score and bucket a single row in-place (mutates the passed object).
 * Call this after enrichment and after assigning stableIndex.
 */
export function applyScoring(row: EnrichedQueueRow): EnrichedQueueRow {
  row.priorityScore = scoreRow(row);
  row.bucket = bucketFromScore(row.priorityScore);
  row.rankReasons = topReasons(row);
  return row;
}

/**
 * Apply a filter predicate to a list of enriched rows.
 */
export function applyFilter(rows: EnrichedQueueRow[], filter: QueueFilter): EnrichedQueueRow[] {
  switch (filter) {
    case 'all':           return rows;
    case 'needs_fa':      return rows.filter((r) => r.handoff === 'needs_fa' || r.handoff === 'escalated');
    case 'ai_handling':   return rows.filter((r) => r.handoff === 'ai_in_progress');
    case 'waiting_10m':   return rows.filter((r) => r.waitingMinutes > 10);
    case 'upcoming_call': return rows.filter((r) => r.hasUpcomingCall);
  }
}

/**
 * Build the three QueueSections from a filtered, scored list of rows.
 * Rows within each section are sorted by stableIndex (ascending) to prevent
 * visual jitter — not by live score.
 */
export function buildSections(rows: EnrichedQueueRow[]): QueueSection[] {
  const buckets: Record<QueueBucket, EnrichedQueueRow[]> = {
    now:   [],
    next:  [],
    later: [],
  };

  for (const row of rows) {
    buckets[row.bucket].push(row);
  }

  const sortByStable = (a: EnrichedQueueRow, b: EnrichedQueueRow) =>
    a.stableIndex - b.stableIndex;

  return [
    { bucket: 'now',   label: 'Now',   rows: buckets.now.sort(sortByStable) },
    { bucket: 'next',  label: 'Next',  rows: buckets.next.sort(sortByStable) },
    { bucket: 'later', label: 'Later', rows: buckets.later.sort(sortByStable) },
  ];
}
